/**
 * useShellOverwatch - kill zones: declaring one, and noticing who walks in.
 *
 * - `handleOverwatch()` (v8.308.0): the bearing comes from the MEASURING TAPE
 *   when one is drawn - so you can cover an empty doorway - and falls back to
 *   whoever is targeted. The fire mode is whatever the weapon is set to. It
 *   is a CONDITION, so it syncs, undoes, wears a chip and expires with the
 *   other stances; `condType` collapses every Ov: code to 'Ov', which keeps
 *   it to one arc per person.
 * - `handleTokenSettled(movedId)`: WHO WALKED INTO WHOSE ARC. Called from the
 *   battlefield's LOCAL move-commit, not an effect diffing positions: an
 *   effect runs on every client and the log syncs, so the table would read
 *   the same line once per player. ANNOUNCE-ONLY - no interrupt, no prompt,
 *   no stolen turn; the trigger conditions are the table's. Each watcher,
 *   mover and round is announced once.
 *
 * Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { useCallback, useRef } from 'react';
import { condType } from '../engine/conditions';
import { combatantsInCone } from '../engine/battlefield/area';
import { bearingBetween } from '../engine/battlefield/facing';
import { encodeOverwatch, parseOverwatch, overwatchOf, announceKey, OVERWATCH_CONE_DEGREES } from '../engine/overwatch';
import { EMPTY_SOLIDS, aimPoint } from './useShellAreaTargeting';
import { hasCondition, infoLine } from './shellCombatant';

const NO_DIRECTION =
  '👁 Overwatch needs a direction - draw the tape across the ground you mean to cover, or pick a target.';

/** The declaration's log line. */
function overwatchText(actor, zone) {
  return `👁 ${actor.name || 'The watcher'} covers a ${OVERWATCH_CONE_DEGREES}° arc on ${zone.heading}° (${zone.mode}). Trigger conditions are the table's to declare.`;
}

/** Declare the arc, or say why there is no direction to declare it in. */
function declareOverwatch(deps) {
  const { actor, tokens } = deps;
  const shooterToken = tokens[deps.actorId];
  const at = aimPoint(deps.measured, tokens[deps.activeTab]);
  if (!shooterToken || !at) {
    deps.addRollNarrative(infoLine(NO_DIRECTION));
    return;
  }
  const code = encodeOverwatch({ heading: bearingBetween(shooterToken, at), mode: deps.fireMode });
  const zone = parseOverwatch(code);
  deps.capturePreActionSnapshot();
  deps.pushHistory();
  deps.updateCombatantState(actor.id, (st) => {
    const conds = (st.conditions ?? []).filter((c) => condType(c) !== 'Ov');
    return { ...st, conditions: [...conds, code] };
  });
  deps.addRollNarrative(infoLine(overwatchText(actor, zone)));
}

/** Is the mover inside this watcher's arc? Null when the watcher holds no arc or has no marker. */
function arcCatches(watcher, mover, field) {
  const zone = overwatchOf(watcher.state?.conditions);
  const watcherToken = zone ? field.tokens[watcher.id] : null;
  if (!watcherToken) return null;
  const caught = combatantsInCone(
    { x: watcherToken.x, z: watcherToken.z, elevation: watcherToken.elevation },
    zone.heading,
    field.watcherRange(watcher.id) ?? 30,
    OVERWATCH_CONE_DEGREES,
    [mover],
    { solids: field.solids, includeHidden: true },
  );
  return caught.length ? zone : null;
}

/** The watcher's arc if the mover is newly in it this round - marked announced - else null. */
function newlyCaughtBy(watcher, movedId, mover, field) {
  if (watcher.id === movedId) return null;
  const zone = arcCatches(watcher, mover, field);
  if (!zone) return null;
  const key = announceKey(watcher.id, movedId, field.round);
  if (field.announced.has(key)) return null;
  field.announced.add(key);
  return zone;
}

/** Announce, once per watcher, mover and round, everyone whose arc the mover stepped into. */
export function announceKillZones(movedId, field) {
  const mover = field.tokens[movedId];
  if (!mover) return;
  for (const watcher of field.roster) {
    const zone = newlyCaughtBy(watcher, movedId, mover, field);
    if (!zone) continue;
    const moving = field.roster.find((c) => c.id === movedId);
    const moverName = moving?.name || movedId;
    // A Tactical Advance (2026-10-26): he counts as in cover for the move it was for - the announcement says so.
    const advancing = hasCondition(moving?.state?.conditions, 'Ta');
    const cover = advancing ? ' He made a Tactical Advance: he counts as in cover.' : '';
    field.addRollNarrative(
      infoLine(
        `\uD83D\uDC41 ${moverName} steps into ${watcher.name || watcher.id}'s kill zone (${zone.mode}).${cover}`,
      ),
    );
  }
}

/** The settle callback; a watcher's reach is their armed weapon's range, read at call time. */
function useTokenSettled({
  tokens,
  roster,
  ignoreTerrain,
  mapSolids,
  addRollNarrative,
  turnTrackerState,
  effectiveSlot,
}) {
  const announced = useRef(new Set());
  const watcherRangeRef = useRef(null);
  watcherRangeRef.current = () => effectiveSlot?.range || 30;
  return useCallback(
    (movedId) =>
      announceKillZones(movedId, {
        tokens,
        roster,
        solids: ignoreTerrain ? EMPTY_SOLIDS : mapSolids,
        round: turnTrackerState?.round ?? 0,
        announced: announced.current,
        watcherRange: (id) => watcherRangeRef.current?.(id),
        addRollNarrative,
      }),
    [tokens, roster, ignoreTerrain, mapSolids, addRollNarrative, turnTrackerState],
  );
}

/**
 * @param {object} args - actor, actorId, tokens, activeTab, fireMode,
 *   measureLineRef, roster, ignoreTerrain, mapSolids, turnTrackerState,
 *   effectiveSlot, and the writers: updateCombatantState, addRollNarrative,
 *   capturePreActionSnapshot, pushHistory.
 * @returns {{ handleOverwatch: Function, handleTokenSettled: Function }}
 */
export default function useShellOverwatch(args) {
  const { actor, actorId, tokens, activeTab, fireMode, measureLineRef } = args;
  const { updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory } = args;
  const handleOverwatch = useCallback(() => {
    if (!actor) return;
    const writers = { updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory };
    declareOverwatch({ actor, actorId, tokens, activeTab, fireMode, measured: measureLineRef.current?.to, ...writers });
  }, [
    actor,
    actorId,
    tokens,
    activeTab,
    fireMode,
    measureLineRef,
    updateCombatantState,
    addRollNarrative,
    capturePreActionSnapshot,
    pushHistory,
  ]);
  const handleTokenSettled = useTokenSettled(args);
  return { handleOverwatch, handleTokenSettled };
}
